<template>
  <div class="mb-4" style="border: 1px solid rgba(0,0,0,0.12); border-radius: 8px; padding: 10px 12px; background: rgba(0,0,0,0.015)">
    <div class="d-flex justify-space-between align-center mb-2">
      <p class="caption font-weight-bold text-uppercase ma-0" style="letter-spacing: 0.05em; font-size: 10px; color: rgba(0,0,0,0.7)">Metas Calculadas</p>
      <v-btn v-if="!origemInformada" x-small outlined color="primary" :loading="carregando" :disabled="!podeCalcular" @click="recalcular">
        <v-icon x-small class="mr-1">mdi-refresh</v-icon>Recalcular
      </v-btn>
    </div>

    <v-alert v-if="!podeExibirTabela" type="info" dense text class="caption mb-0">
      {{ origemInformada
        ? "Selecione ao menos um participante para informar as metas."
        : "Selecione ao menos um mês de referência e um participante para calcular as metas." }}
    </v-alert>

    <template v-else>
      <v-simple-table dense>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left caption">Colaborador</th>
              <th v-if="!origemInformada" class="text-end caption">Média</th>
              <th v-if="!origemInformada" class="text-end caption">{{ tipoProgressivo ? "% aplicado" : `+${percentualExibicaoFormatado}%` }}</th>
              <th class="text-end caption" style="width: 120px">Meta</th>
              <th v-if="usaBonusReferencia" class="text-end caption">{{ rotuloBonusReferencia }}</th>
              <th class="text-left caption" style="width: 130px"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="linha in linhas" :key="linha.id_colaborador">
              <td class="caption">{{ linha.nome }}</td>
              <td v-if="!origemInformada" class="text-end caption">{{ formatar(linha.media) }}</td>
              <td v-if="!origemInformada" class="text-end caption grey--text">
                {{ tipoProgressivo ? formatarPercentualAplicado(linha.percentualAplicado) : formatar(linha.comCrescimento) }}
              </td>
              <td class="text-end">
                <v-text-field
                  :value="linha.meta"
                  @input="editarMeta(linha.id_colaborador, $event)"
                  type="number"
                  dense
                  hide-details
                  single-line
                  reverse
                  style="font-size: 13px"
                ></v-text-field>
              </td>
              <!-- Enquanto a comissao esta em voo, spinner em vez de numero. Sem
                   isto a celula mostrava R$ 0,00 durante a requisicao, que parece
                   um valor apurado — e foi por isso que deu para salvar no meio
                   da busca sem perceber. -->
              <td v-if="usaBonusReferencia" class="text-end caption">
                <v-progress-circular v-if="carregandoComissaoReferencia" indeterminate size="14" width="2" color="primary" />
                <template v-else>{{ formatarValorReferencia(linha.bonusReferencia) }}</template>
              </td>
              <td>
                <template v-if="!origemInformada && linha.manual">
                  <v-chip x-small color="blue lighten-4" class="font-weight-bold">manual</v-chip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        icon
                        x-small
                        class="ml-1"
                        :loading="revertendo === linha.id_colaborador"
                        :disabled="!podeCalcular || carregando"
                        @click="reverterMeta(linha.id_colaborador)"
                      >
                        <v-icon x-small>mdi-restore</v-icon>
                      </v-btn>
                    </template>
                    <span>Voltar para a meta calculada</span>
                  </v-tooltip>
                </template>
                <v-chip v-else-if="!origemInformada && linha.piso_aplicado" x-small color="amber lighten-3" class="font-weight-bold">piso</v-chip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <p v-if="!origemInformada" class="caption grey--text ma-0 mt-2" style="line-height: 1.4">
        Metas editadas à mão ficam marcadas como <strong>manual</strong> e são preservadas ao recalcular.
        Use <v-icon x-small>mdi-restore</v-icon> ao lado da marca para voltar à meta calculada daquele participante.
      </p>
      <p v-else class="caption grey--text ma-0 mt-2" style="line-height: 1.4">
        Informe a meta de cada participante.
      </p>
    </template>
  </div>
</template>

<script>
import { getHistoricoConsorcio, getComissaoP2Consorcio } from "@/services/consorcio";
import { calcularMetasColaboradores, resolverPercentualPorFaixa } from "./metaMediaUtils";
import { mesclarMetasCalculadas, registrarEdicaoManual, reverterEdicaoManual } from "./tabelaMetasCalculadasUtils";
import { calcularBonusReferencia, extrairCompetenciaFechamento, formatarCompetencia } from "./bonusReferenciaUtils";
import comissao from "@/services/http/comissaoService";
import { getApiError } from "@/services/http";

export default {
  name: "TabelaMetasCalculadas",

  props: {
    form: { type: Object, required: true },
    // So usado para resolver a competencia (mes_ref/ano_ref) do bonus por
    // "% sobre referencia" — ver buscarComissaoReferenciaSeNecessario.
    campanha: { type: Object, default: () => ({}) }
  },

  data() {
    return {
      carregando: false,
      // id_colaborador da linha cujo botao de reverter esta em andamento.
      revertendo: null,
      historico: [],
      // Mapa { id_colaborador (string): valor_comissao } da comissao P2 na
      // competencia da campanha — ver getComissaoP2Consorcio. Colaborador
      // sem comissao no periodo fica ausente do mapa; quem le trata ausente
      // como zero (ver calcularBonusReferencia).
      comissaoPorColaborador: {},
      competenciaBonusReferencia: null,
      // Trava para nao refazer a requisicao a cada troca de percentual nem
      // a cada re-render — busca uma vez por montagem do componente (que
      // ja se remonta sozinho quando colaboradoresSelecionados muda, ver
      // o watch homonimo em SubEtapaCalculoModelo).
      buscouComissaoReferencia: false,
      carregandoComissaoReferencia: false
    };
  },

  computed: {
    // Origem 1 (Informada): tudo digitado a mao, sem historico envolvido.
    origemInformada() {
      return this.form.origemMeta === 1;
    },

    // So faz sentido calcular (e so a Media historica exige) com mes de
    // referencia selecionado; a origem Informada nunca calcula nada, entao
    // nunca habilita o recalculo, mesmo que mesesReferencia tenha sobrado
    // de uma troca de origem anterior.
    podeCalcular() {
      return (
        this.form.origemMeta === 2 &&
        (this.form.mesesReferencia || []).length > 0 &&
        (this.form.colaboradoresSelecionados || []).length > 0
      );
    },

    // Controla a exibicao da propria tabela: na origem Informada basta ter
    // participante selecionado (nao ha media para calcular); na Media
    // historica continua exigindo tambem o mes de referencia.
    podeExibirTabela() {
      return this.origemInformada
        ? (this.form.colaboradoresSelecionados || []).length > 0
        : this.podeCalcular;
    },

    percentualExibicao() {
      return parseFloat(this.form.percentualCrescimento) || 0;
    },

    percentualExibicaoFormatado() {
      return this.percentualExibicao.toLocaleString("pt-BR");
    },

    tipoProgressivo() {
      return Number(this.form.tipoCrescimentoMedia) === 2;
    },

    // Coluna do bonus por referencia: so com a meta do colaborador ligada
    // (garantido pelo v-if do proprio componente no pai) e com a origem do
    // bonus em "% sobre referencia" — na origem Informado nao ha comissao
    // nenhuma para buscar nem mostrar.
    usaBonusReferencia() {
      return Number(this.form.origemBonusColaborador) === 2;
    },

    rotuloBonusReferencia() {
      return `Bônus ref. (${formatarCompetencia(this.competenciaBonusReferencia)})`;
    },

    // A busca terminou e deu certo. Espelha a condicao que montarExtrasMeta usa
    // (form.comissaoParticipantes != null) — as duas sao escritas juntas no
    // sucesso, e e isso que garante que a tela mostre o mesmo numero que vai
    // ser gravado. Em voo e erro caem no valor gravado.
    comissaoDisponivel() {
      return this.buscouComissaoReferencia && !this.carregandoComissaoReferencia;
    },

    linhas() {
      const metas = this.form.metasCalculadas || {};
      return (this.form.colaboradoresSelecionados || []).map(c => {
        const registro = metas[c.id_colaborador] || {};
        // Sem media (origem Informada nunca tem; origem Media historica
        // pode nao ter pra um colaborador especifico) fica null, nao 0 —
        // um zero de verdade ficaria indistinguivel de "sem media". formatar()
        // trata o null exibindo travessao em vez de "0,0".
        const media = registro.media != null ? registro.media : null;
        const comissaoColaborador = this.comissaoPorColaborador[String(c.id_colaborador)];
        // No tipo Progressivo, o percentual varia por colaborador — resolvido
        // ao vivo a partir das faixas atuais, e nao lido de metasCalculadas
        // (que so guarda media/meta/manual): assim a coluna reflete a faixa
        // vigente mesmo numa meta reaberta, cujo calculo original foi feito
        // em outra sessao. Ver resolverPercentualPorFaixa em metaMediaUtils.js.
        const percentualAplicado = media == null
          ? null
          : this.tipoProgressivo
            ? resolverPercentualPorFaixa(media, this.form.faixasCrescimento)
            : this.percentualExibicao;
        return {
          id_colaborador: c.id_colaborador,
          nome: c.colaborador || c.nome || String(c.id_colaborador),
          media,
          percentualAplicado,
          comCrescimento: media != null && percentualAplicado != null ? media * (1 + percentualAplicado / 100) : null,
          meta: registro.meta != null ? registro.meta : "",
          manual: !!registro.manual,
          piso_aplicado: !!registro.piso_aplicado,
          // Com a comissao em maos: recalcula localmente (percentual x comissao
          // em cache) — trocar o percentual nunca refaz a requisicao, ver
          // buscarComissaoReferenciaSeNecessario.
          //
          // Sem a comissao (busca falhou, ou meta reaberta e o usuario ainda nao
          // chegou aqui): mostra o valor GRAVADO, que e exatamente o que o
          // payload vai reenviar nesse estado. A tela e a gravacao precisam
          // concordar — antes a celula mostrava R$ 0,00 enquanto o payload
          // mandava outra coisa.
          bonusReferencia: this.comissaoDisponivel
            ? calcularBonusReferencia(this.form.comissaoPercentual, comissaoColaborador)
            : (registro.bonusGravado != null ? Number(registro.bonusGravado) : 0)
        };
      });
    },

    // Espelha a regra de todasMetasParticipantesPreenchidas em
    // metaColaboradorValidacaoUtils.js (meta vazia ou <= 0 bloqueia o
    // Salvar). A celula em si nao mostra erro por linha. Ate a Task 15 este
    // computed alimentava um v-alert type="error" proprio aqui no rodape da
    // tabela — removido porque a mesma contagem agora entra no aviso neutro
    // unico de SubEtapaCalculoModelo (pendenciasParticipante), junto com as
    // pendencias de bonus e parametros da media: reaproveitar em vez de
    // multiplicar dois avisos quase iguais. O computed em si continua aqui e
    // testado isoladamente (tabelaMetasCalculadas.spec.js) porque
    // pendenciasMetaColaborador espelha a mesma regra de forma independente.
    nParticipantesSemMeta() {
      return this.linhas.filter(l => {
        if (l.meta === "" || l.meta == null) return true;
        const valor = parseFloat(l.meta);
        return isNaN(valor) || valor <= 0;
      }).length;
    }
  },

  watch: {
    // Troca de Informado (1) para % sobre referencia (2) busca a comissao,
    // se ainda nao tiver buscado. Voltar para Informado e trocar de novo
    // para 2 nao busca de novo (buscouComissaoReferencia ja true) — so uma
    // remontagem do componente (colaboradoresSelecionados mudou, ver
    // SubEtapaCalculoModelo) reseta a trava e permite buscar de novo.
    "form.origemBonusColaborador"() {
      this.buscarComissaoReferenciaSeNecessario();
    }
  },

  created() {
    // Cobre o caso de abrir a tela ja com origemBonusColaborador === 2 (
    // edicao de uma meta salva nessa origem) — o watch acima so dispara em
    // mudanca, nao no valor inicial.
    this.buscarComissaoReferenciaSeNecessario();
  },

  methods: {
    formatar(n) {
      // null representa ausencia de media (ver o comment em linhas()) —
      // travessao em vez de "0,0", que pareceria uma media real de zero.
      if (n == null) return "—";
      return Number(parseFloat(n) || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      });
    },

    // Sem comissao na competencia o bonus vale R$ 0,00, nao travessao: a
    // apuracao atualiza esse valor conforme a comissao P2 do mes cresce, entao
    // hoje o participante tem bonus zero — ver calcularBonusReferencia.
    formatarValorReferencia(n) {
      return Number(parseFloat(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },

    // null so deveria acontecer se as faixas nao cobrirem a media do
    // colaborador (bug de cadastro, ja bloqueado pela validacao) — travessao
    // em vez de "0%", que pareceria uma faixa real de crescimento zero.
    formatarPercentualAplicado(percentual) {
      if (percentual == null) return "—";
      return `${Number(percentual).toLocaleString("pt-BR")}%`;
    },

    // Resolve a competencia da campanha (mes_ref/ano_ref) via
    // id_tipo_fechamento e busca, numa unica requisicao, a comissao P2 de
    // todos os participantes selecionados naquela competencia. So roda uma
    // vez por montagem (buscouComissaoReferencia) — trocar o percentual
    // depois so multiplica localmente em linhas(), nunca busca de novo.
    async buscarComissaoReferenciaSeNecessario() {
      if (!this.usaBonusReferencia) return;
      if (this.buscouComissaoReferencia) return;
      this.buscouComissaoReferencia = true;

      this.carregandoComissaoReferencia = true;
      try {
        const competencia = await this.buscarCompetenciaCampanha();
        if (!competencia) return;
        this.competenciaBonusReferencia = competencia;

        const { data } = await getComissaoP2Consorcio({
          id_colaboradores: (this.form.colaboradoresSelecionados || []).map(c => c.id_colaborador),
          ano_ref: competencia.ano_ref,
          mes_ref: competencia.mes_ref
        });
        this.comissaoPorColaborador = data.data || {};
        // Publica no form para o custo estimado (calcCustoParticipante) poder
        // somar o bonus por "% sobre referencia" nos dois lugares que o
        // calculam — o card desta etapa e o resumo lateral do modal. Nao entra
        // no payload: montarExtrasMeta monta as chaves uma a uma.
        this.$set(this.form, "comissaoParticipantes", this.comissaoPorColaborador);
      } catch (e) {
        // Permite tentar de novo numa proxima troca de origem, em vez de
        // deixar a coluna presa em "—" pro resto da sessao.
        this.buscouComissaoReferencia = false;
        this.$notify({ type: "error", text: getApiError(e, "Erro ao buscar a comissão de referência") });
      } finally {
        this.carregandoComissaoReferencia = false;
      }
    },

    // Mesmo padrao de comissao().tipoFechamento().show(...) ja usado em
    // FormCadastroCampanha.findIdTipoFechamento e
    // SectionPublicoMeta.carregarSemanasCampanha: filtra por
    // id_tipo_fechamento e le mes_ref/ano_ref do primeiro item devolvido.
    async buscarCompetenciaCampanha() {
      if (!this.campanha || !this.campanha.id_tipo_fechamento) return null;
      const { data } = await comissao().tipoFechamento().show({
        id_tipo_fechamento: this.campanha.id_tipo_fechamento
      });
      return extrairCompetenciaFechamento(data.data);
    },

    // Busca o realizado dos meses de referencia e guarda em this.historico.
    // Separado de recalcular porque reverterMeta tambem precisa dele: ao
    // reabrir uma meta ja salva o historico comeca vazio (as metas vem do
    // banco, nao de um calculo desta sessao), e sem ele nao ha para onde
    // voltar a meta manual.
    async carregarHistorico() {
      const { data } = await getHistoricoConsorcio({
        id_colaboradores: this.form.colaboradoresSelecionados.map(c => c.id_colaborador),
        meses: this.form.mesesReferencia
      });
      this.historico = data.data || [];
    },

    async recalcular() {
      if (!this.podeCalcular) return;
      this.carregando = true;
      try {
        await this.carregarHistorico();
        this.aplicarCalculo();
      } catch (e) {
        this.$notify({ type: "error", text: getApiError(e, "Erro ao buscar o histórico de consórcio") });
      } finally {
        this.carregando = false;
      }
    },

    // Recalcula todos menos os editados a mao.
    aplicarCalculo() {
      const calculadas = calcularMetasColaboradores(
        this.historico,
        this.form.percentualCrescimento,
        this.form.valorMinimoMeta,
        this.tipoProgressivo ? this.form.faixasCrescimento : null
      );
      const novo = mesclarMetasCalculadas(this.form.metasCalculadas, calculadas);
      this.$set(this.form, "metasCalculadas", novo);
    },

    editarMeta(idColaborador, valor) {
      const novo = registrarEdicaoManual(this.form.metasCalculadas, idColaborador, valor);
      this.$set(this.form, "metasCalculadas", novo);
    },

    // Desfaz a edicao manual de UMA linha. O Recalcular preserva as metas
    // manuais de proposito, entao esta e a unica saida para quem digitou um
    // valor e quer o calculado de volta sem mexer nas metas dos outros.
    async reverterMeta(idColaborador) {
      if (!this.podeCalcular) return;
      this.revertendo = idColaborador;
      try {
        // Meta salva reaberta nao tem historico em memoria — busca sob demanda.
        if (!this.historico.length) await this.carregarHistorico();

        const calculadas = calcularMetasColaboradores(
          this.historico,
          this.form.percentualCrescimento,
          this.form.valorMinimoMeta,
          this.tipoProgressivo ? this.form.faixasCrescimento : null
        );
        const novo = reverterEdicaoManual(this.form.metasCalculadas, idColaborador, calculadas);
        // null = sem meta calculada para esse colaborador (sem realizado nos
        // meses de referencia) — avisa em vez de fingir que reverteu.
        if (novo === null) {
          this.$notify({
            type: "warning",
            text: "Sem realizado nos meses de referência para este participante — não há meta calculada para restaurar."
          });
          return;
        }
        this.$set(this.form, "metasCalculadas", novo);
      } catch (e) {
        this.$notify({ type: "error", text: getApiError(e, "Erro ao buscar o histórico de consórcio") });
      } finally {
        this.revertendo = null;
      }
    }
  }
};
</script>
